import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import type { Session, User } from '@supabase/supabase-js'
import { getDb } from '../lib/db'
import { getSupabase } from '../lib/supabase'
import type { MembershipWithOrg } from '../lib/types'

export interface AuthContextValue {
  session: Session | null
  user: User | null
  /** True until the initial session check resolves. */
  loading: boolean
  memberships: MembershipWithOrg[]
  membershipsLoading: boolean
  membershipsError: string | null
  refreshMemberships: () => Promise<void>
  signOut: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null)
  const [loading, setLoading] = useState(true)
  const [memberships, setMemberships] = useState<MembershipWithOrg[]>([])
  const [membershipsLoading, setMembershipsLoading] = useState(false)
  const [membershipsError, setMembershipsError] = useState<string | null>(null)

  // Monotonic id so a slow response for a previous user can never clobber the
  // current user's memberships after a fast account switch.
  const membershipRequestRef = useRef(0)

  // Session tracking. NOTE: memberships are loaded in the effect below (never
  // inside onAuthStateChange) — awaiting supabase calls inside the auth
  // callback can deadlock the auth lock.
  useEffect(() => {
    const supabase = getSupabase()

    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session)
      setLoading(false)
    })

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, next) => {
      setSession(next)
      setLoading(false)
    })

    return () => subscription.unsubscribe()
  }, [])

  const userId = session?.user.id ?? null

  const refreshMemberships = useCallback(async () => {
    const requestId = ++membershipRequestRef.current

    if (!userId) {
      setMemberships([])
      setMembershipsError(null)
      setMembershipsLoading(false)
      return
    }

    setMembershipsLoading(true)
    setMembershipsError(null)
    try {
      const rows = await getDb().listMyMemberships(userId)
      if (requestId === membershipRequestRef.current) setMemberships(rows)
    } catch (err) {
      if (requestId === membershipRequestRef.current) {
        setMemberships([])
        setMembershipsError(err instanceof Error ? err.message : 'Failed to load memberships')
      }
    } finally {
      if (requestId === membershipRequestRef.current) setMembershipsLoading(false)
    }
  }, [userId])

  useEffect(() => {
    void refreshMemberships()
  }, [refreshMemberships])

  const signOut = useCallback(async () => {
    await getSupabase().auth.signOut()
  }, [])

  const value = useMemo<AuthContextValue>(
    () => ({
      session,
      user: session?.user ?? null,
      loading,
      memberships,
      membershipsLoading,
      membershipsError,
      refreshMemberships,
      signOut,
    }),
    [session, loading, memberships, membershipsLoading, membershipsError, refreshMemberships, signOut],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>')
  return ctx
}
